import { useEffect, useRef, useState } from 'react'
import api from '../../lib/api'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Mail, RefreshCw, CheckCircle2 } from 'lucide-react'

interface VerifyEmailStepProps {
  email: string
  onVerified: () => void
}

const POLL_INTERVAL_MS = 5000

export default function VerifyEmailStep({ email, onVerified }: VerifyEmailStepProps) {
  const [isResending, setIsResending] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const { toast } = useToast()
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const checkVerified = async (silent = true): Promise<boolean> => {
    try {
      const res = await api.get('/api/auth/me')
      if (res.data?.agent?.emailVerified) {
        onVerified()
        return true
      }
      if (!silent) {
        toast({
          title: 'Still waiting',
          description: 'We haven\'t received your confirmation yet. Check your inbox (and spam folder).',
        })
      }
      return false
    } catch (err) {
      return false
    }
  }

  useEffect(() => {
    checkVerified(true)
    pollTimerRef.current = setInterval(() => { checkVerified(true) }, POLL_INTERVAL_MS)
    const onFocus = () => { checkVerified(true) }
    window.addEventListener('focus', onFocus)
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const t = setTimeout(() => setCooldownSeconds(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldownSeconds])

  const handleResend = async () => {
    if (cooldownSeconds > 0 || isResending) return
    setIsResending(true)
    try {
      await api.post('/api/auth/resend-verification')
      toast({
        title: 'Email sent',
        description: `We sent a fresh verification link to ${email}.`,
      })
      setCooldownSeconds(30)
    } catch (error: any) {
      const message = error.response?.data?.message || 'Could not resend verification email'
      toast({ title: 'Error', description: message, variant: 'destructive' })
    } finally {
      setIsResending(false)
    }
  }

  const handleCheckNow = async () => {
    setIsChecking(true)
    await checkVerified(false)
    setIsChecking(false)
  }

  return (
    <div className="max-w-md mx-auto text-center">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/15 border border-primary/30 mb-5">
        <Mail className="h-7 w-7 text-primary" />
      </div>

      <h2 className="text-xl sm:text-2xl font-heading font-semibold text-white tracking-tight">
        Check your email
      </h2>
      <p className="text-white/60 mt-2 text-sm">
        We sent a verification link to
      </p>
      <p className="text-white font-medium text-sm mt-0.5 break-all">{email}</p>
      <p className="text-white/50 mt-4 text-xs leading-relaxed">
        Click the link in the email to verify your address.<br />
        This page will update automatically once you do.
      </p>

      <div className="mt-8 space-y-3">
        <Button
          type="button"
          onClick={handleCheckNow}
          disabled={isChecking}
          className="w-full h-11 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-all border-0"
        >
          {isChecking ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              I've verified my email
            </>
          )}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={handleResend}
          disabled={isResending || cooldownSeconds > 0}
          className="w-full h-10 text-white/70 hover:text-white"
        >
          {isResending
            ? 'Sending...'
            : cooldownSeconds > 0
              ? `Resend in ${cooldownSeconds}s`
              : "Didn't receive it? Resend email"}
        </Button>
      </div>

      <p className="text-xs text-white/40 mt-8 leading-relaxed">
        Wrong address? <button
          type="button"
          onClick={() => {
            localStorage.removeItem('app_auth_token')
            localStorage.removeItem('app_onboarding_complete')
            window.location.href = '/onboarding/step/1'
          }}
          className="text-primary hover:underline"
        >
          Start over
        </button>
      </p>
    </div>
  )
}
